/* tslint:disable */
/* eslint-disable */

import { mergeMap as _observableMergeMap, catchError as _observableCatch } from 'rxjs/operators';
import { Observable, throwError as _observableThrow, of as _observableOf } from 'rxjs';
import {Injectable, Inject, Injector} from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse, HttpResponseBase } from '@angular/common/http';
import {
    CommissionProviderImportDto,
    RollDetails,
    UpdateCommissionReviewFileInput,
    PartnerManagementReportOutput,
    ChurnDashboardStatisticsDto,
    CommissionChurnDto,
    ActivityPartnerSalesDashboardOutput,
    CommissionWorkbookListDto,
    CommissionOrderVarianceDto,
} from '@shared/service-proxies/service-proxies';
import {ENVIRONMENT} from "@shared/utils/environment";

/**
 * This class is used to call a backend that is NOT behind Ambassador. The reason is, Ambassador will fail the requests after 60 seconds.
 * and the listed methods are known for taking more than 60 seconds some times.
 * This class will be eventually removed when the backend is fixed and the methods take less time.
 */
@Injectable()
export class BypassedCommissionServiceProxy {
    private http: HttpClient;
    protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;
    private readonly baseUrl: string;

    constructor(@Inject(HttpClient) http: HttpClient, private readonly injector: Injector) {
        this.http = http;
        const { backendUrlOutsideLoadBalancer} = this.injector.get(ENVIRONMENT);
        this.baseUrl = backendUrlOutsideLoadBalancer;
    }

    /**
     * @param month (optional)
     * @param year (optional)
     * @param partnerAcquisitionIds (optional)
     * @param uploaderType (optional)
     * @return Success
     */
    getPayoutCommissionAsCSVFile(
        month: number | undefined,
        year: number | undefined,
        partnerAcquisitionIds: string | undefined,
        uploaderType: ReportUploaderType | undefined,
        token: string
    ): Observable<FileDto> {
        let url_ = this.baseUrl + '/api/services/app/Commission/GetPayoutCommissionAsCSVFile?';
        if (month === null) throw new Error("The parameter 'month' cannot be null.");
        else if (month !== undefined) url_ += 'month=' + encodeURIComponent('' + month) + '&';
        if (year === null) throw new Error("The parameter 'year' cannot be null.");
        else if (year !== undefined) url_ += 'year=' + encodeURIComponent('' + year) + '&';
        if (partnerAcquisitionIds === null) throw new Error("The parameter 'partnerAcquisitionIds' cannot be null.");
        else if (partnerAcquisitionIds !== undefined)
            url_ += 'partnerAcquisitionIds=' + encodeURIComponent('' + partnerAcquisitionIds) + '&';
        if (uploaderType === null) throw new Error("The parameter 'uploaderType' cannot be null.");
        else if (uploaderType !== undefined) url_ += 'uploaderType=' + encodeURIComponent('' + uploaderType) + '&';
        url_ = url_.replace(/[?&]$/, '');

        let options_: any = {
            observe: 'response',
            responseType: 'blob',
            withCredentials: true,
            headers: new HttpHeaders({
                Accept: 'text/plain',
                'Ad-Authorization': token,
            }),
        };

        return this.http
            .request('get', url_, options_)
            .pipe(
                _observableMergeMap((response_: any) => {
                    return this.processGetPayoutCommissionAsCSVFile(response_);
                })
            )
            .pipe(
                _observableCatch((response_: any) => {
                    if (response_ instanceof HttpResponseBase) {
                        try {
                            return this.processGetPayoutCommissionAsCSVFile(<any>response_);
                        } catch (e) {
                            return <Observable<FileDto>>(<any>_observableThrow(e));
                        }
                    } else return <Observable<FileDto>>(<any>_observableThrow(response_));
                })
            );
    }

    protected processGetPayoutCommissionAsCSVFile(response: HttpResponseBase): Observable<FileDto> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body : (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {};
        if (response.headers) {
            for (let key of response.headers.keys()) {
                _headers[key] = response.headers.get(key);
            }
        }
        if (status === 200) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    let result200: any = null;
                    let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
                    result200 = FileDto.fromJS(resultData200);
                    return _observableOf(result200);
                })
            );
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    return throwException('An unexpected server error occurred.', status, _responseText, _headers);
                })
            );
        }
        return _observableOf<FileDto>(<any>null);
    }

    /**
     * @param body (optional)
     * @return Success
     */
    resetCommissionReviewFile(body: ResetCommissionReviewFileInput | undefined, token: string): Observable<boolean> {
        let url_ = this.baseUrl + '/api/services/app/Commission/ResetCommissionReviewFile';
        url_ = url_.replace(/[?&]$/, '');

        const content_ = JSON.stringify(body);

        let options_: any = {
            body: content_,
            observe: 'response',
            responseType: 'blob',
            withCredentials: true,
            headers: new HttpHeaders({
                'Content-Type': 'application/json-patch+json',
                Accept: 'text/plain',
                'Ad-Authorization': token,
            }),
        };

        return this.http
            .request('post', url_, options_)
            .pipe(
                _observableMergeMap((response_: any) => {
                    return this.processResetCommissionReviewFile(response_);
                })
            )
            .pipe(
                _observableCatch((response_: any) => {
                    if (response_ instanceof HttpResponseBase) {
                        try {
                            return this.processResetCommissionReviewFile(<any>response_);
                        } catch (e) {
                            return <Observable<boolean>>(<any>_observableThrow(e));
                        }
                    } else return <Observable<boolean>>(<any>_observableThrow(response_));
                })
            );
    }

    protected processResetCommissionReviewFile(response: HttpResponseBase): Observable<boolean> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body : (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {};
        if (response.headers) {
            for (let key of response.headers.keys()) {
                _headers[key] = response.headers.get(key);
            }
        }
        if (status === 200) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    let result200: any = null;
                    let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
                    result200 = resultData200 !== undefined ? resultData200 : <any>null;
                    return _observableOf(result200);
                })
            );
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    return throwException('An unexpected server error occurred.', status, _responseText, _headers);
                })
            );
        }
        return _observableOf<boolean>(<any>null);
    }

    /**
     * @param body (optional)
     * @return Success
     */
    updateCommissionReviewFile(body: UpdateCommissionReviewFileInput | undefined, token: string): Observable<CommissionProviderImportDto> {
        let url_ = this.baseUrl + '/api/services/app/Commission/UpdateCommissionReviewFile';
        url_ = url_.replace(/[?&]$/, '');

        const content_ = JSON.stringify(body);

        let options_: any = {
            body: content_,
            observe: 'response',
            responseType: 'blob',
            withCredentials: true,
            headers: new HttpHeaders({
                'Content-Type': 'application/json-patch+json',
                Accept: 'text/plain',
                'Ad-Authorization': token,
            }),
        };

        return this.http
            .request('put', url_, options_)
            .pipe(
                _observableMergeMap((response_: any) => {
                    return this.processUpdateCommissionReviewFile(response_);
                })
            )
            .pipe(
                _observableCatch((response_: any) => {
                    if (response_ instanceof HttpResponseBase) {
                        try {
                            return this.processUpdateCommissionReviewFile(<any>response_);
                        } catch (e) {
                            return <Observable<CommissionProviderImportDto>>(<any>_observableThrow(e));
                        }
                    } else return <Observable<CommissionProviderImportDto>>(<any>_observableThrow(response_));
                })
            );
    }

    protected processUpdateCommissionReviewFile(response: HttpResponseBase): Observable<CommissionProviderImportDto> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body : (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {};
        if (response.headers) {
            for (let key of response.headers.keys()) {
                _headers[key] = response.headers.get(key);
            }
        }
        if (status === 200) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    let result200: any = null;
                    let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
                    result200 = CommissionProviderImportDto.fromJS(resultData200);
                    return _observableOf(result200);
                })
            );
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    return throwException('An unexpected server error occurred.', status, _responseText, _headers);
                })
            );
        }
        return _observableOf<CommissionProviderImportDto>(<any>null);
    }

    /**
     * @return Success
     */
    getCommissionHoldList(token: string): Observable<CommissionWorkbookListDto[]> {
        let url_ = this.baseUrl + '/api/services/app/Commission/GetCommissionHoldList';
        url_ = url_.replace(/[?&]$/, '');

        let options_: any = {
            observe: 'response',
            responseType: 'blob',
            withCredentials: true,
            headers: new HttpHeaders({
                Accept: 'text/plain',
                'Ad-Authorization': token,
            }),
        };

        return this.http
            .request('get', url_, options_)
            .pipe(
                _observableMergeMap((response_: any) => {
                    return this.processGetCommissionHoldList(response_);
                })
            )
            .pipe(
                _observableCatch((response_: any) => {
                    if (response_ instanceof HttpResponseBase) {
                        try {
                            return this.processGetCommissionHoldList(<any>response_);
                        } catch (e) {
                            return <Observable<CommissionWorkbookListDto[]>>(<any>_observableThrow(e));
                        }
                    } else return <Observable<CommissionWorkbookListDto[]>>(<any>_observableThrow(response_));
                })
            );
    }

    protected processGetCommissionHoldList(response: HttpResponseBase): Observable<CommissionWorkbookListDto[]> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body : (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {};
        if (response.headers) {
            for (let key of response.headers.keys()) {
                _headers[key] = response.headers.get(key);
            }
        }
        if (status === 200) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    let result200: any = null;
                    let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
                    if (Array.isArray(resultData200)) {
                        result200 = [] as any;
                        for (let item of resultData200) result200.push(CommissionWorkbookListDto.fromJS(item));
                    }
                    return _observableOf(result200);
                })
            );
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    return throwException('An unexpected server error occurred.', status, _responseText, _headers);
                })
            );
        }
        return _observableOf<CommissionWorkbookListDto[]>([]);
    }

    /**
     * @param month (optional)
     * @param year (optional)
     * @return Success
     */
     getReportCommissionOrderRevenueVariance(month: number | undefined, year: number | undefined, token: string): Observable<CommissionOrderVarianceDto[]> {
        let url_ = this.baseUrl + "/api/services/app/Commission/GetReportCommissionOrderRevenueVariance?";
        if (month === null)
            throw new Error("The parameter 'month' cannot be null.");
        else if (month !== undefined)
            url_ += "month=" + encodeURIComponent("" + month) + "&";
        if (year === null)
            throw new Error("The parameter 'year' cannot be null.");
        else if (year !== undefined)
            url_ += "year=" + encodeURIComponent("" + year) + "&";
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            observe: "response",
            responseType: "blob",
            withCredentials: true,
            headers: new HttpHeaders({
                "Accept": "text/plain",
                'Ad-Authorization': token,
            })
        };

        return this.http.request("get", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processGetReportCommissionOrderRevenueVariance(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetReportCommissionOrderRevenueVariance(<any>response_);
                } catch (e) {
                    return <Observable<CommissionOrderVarianceDto[]>><any>_observableThrow(e);
                }
            } else
                return <Observable<CommissionOrderVarianceDto[]>><any>_observableThrow(response_);
        }));
    }

    protected processGetReportCommissionOrderRevenueVariance(response: HttpResponseBase): Observable<CommissionOrderVarianceDto[]> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            if (Array.isArray(resultData200)) {
                result200 = [] as any;
                for (let item of resultData200)
                    result200.push(CommissionOrderVarianceDto.fromJS(item));
            }
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<CommissionOrderVarianceDto[]>([]);
    }
}

function throwException(message: string, status: number, response: string, headers: { [key: string]: any }, result?: any): Observable<any> {
    if (result !== null && result !== undefined) return _observableThrow(result);
    else return _observableThrow(new ApiException(message, status, response, headers, null));
}

function blobToText(blob: any): Observable<string> {
    return new Observable<string>((observer: any) => {
        if (!blob) {
            observer.next('');
            observer.complete();
        } else {
            let reader = new FileReader();
            reader.onload = (event) => {
                observer.next((<any>event.target).result);
                observer.complete();
            };
            reader.readAsText(blob);
        }
    });
}

class FileDto implements IFileDto {
    fileName: string;
    fileType: string;
    fileToken: string;

    constructor(data?: IFileDto) {
        if (data) {
            for (var property in data) {
                if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.fileName = _data['fileName'];
            this.fileType = _data['fileType'];
            this.fileToken = _data['fileToken'];
        }
    }

    static fromJS(data: any): FileDto {
        data = typeof data === 'object' ? data : {};
        let result = new FileDto();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data['fileName'] = this.fileName;
        data['fileType'] = this.fileType;
        data['fileToken'] = this.fileToken;
        return data;
    }

    clone(): FileDto {
        const json = this.toJSON();
        let result = new FileDto();
        result.init(json);
        return result;
    }
}

interface IFileDto {
    fileName: string;
    fileType: string;
    fileToken: string;
}

enum ReportUploaderType {
    _0 = 0,
    _1 = 1,
    _2 = 2,
}

class ApiException extends Error {
    message: string;
    status: number;
    response: string;
    headers: { [key: string]: any };
    result: any;

    constructor(message: string, status: number, response: string, headers: { [key: string]: any }, result: any) {
        super();

        this.message = message;
        this.status = status;
        this.response = response;
        this.headers = headers;
        this.result = result;
    }

    protected isApiException = true;

    static isApiException(obj: any): obj is ApiException {
        return obj.isApiException === true;
    }
}

class ResetCommissionReviewFileInput implements IResetCommissionReviewFileInput {
    providerId: number;
    month: number;
    year: number;
    commissionTemplateId: number;
    masterAgencyId: number;

    constructor(data?: IResetCommissionReviewFileInput) {
        if (data) {
            for (var property in data) {
                if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.providerId = _data['providerId'];
            this.month = _data['month'];
            this.year = _data['year'];
            this.commissionTemplateId = _data['commissionTemplateId'];
            this.masterAgencyId = _data['masterAgencyId'];
        }
    }

    static fromJS(data: any): ResetCommissionReviewFileInput {
        data = typeof data === 'object' ? data : {};
        let result = new ResetCommissionReviewFileInput();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data['providerId'] = this.providerId;
        data['month'] = this.month;
        data['year'] = this.year;
        data['commissionTemplateId'] = this.commissionTemplateId;
        data['masterAgencyId'] = this.masterAgencyId;
        return data;
    }

    clone(): ResetCommissionReviewFileInput {
        const json = this.toJSON();
        let result = new ResetCommissionReviewFileInput();
        result.init(json);
        return result;
    }
}

interface IResetCommissionReviewFileInput {
    providerId: number;
    month: number;
    year: number;
    commissionTemplateId: number;
    masterAgencyId: number;
}

export class UserLoginInfoDto implements IUserLoginInfoDto {
    name: string | undefined;
    surname: string | undefined;
    userName: string | undefined;
    emailAddress: string | undefined;
    companyUuid: string | undefined;
    profilePictureId: string | undefined;
    partnerId: number | undefined;
    providerId: number | undefined;
    details: RollDetails[] | undefined;
    partnerName: string | undefined;
    isImpersonator: boolean;
    userExperience: string | undefined;
    isInvestOwner: boolean;
    uuid: string | undefined;
    marketplaceRoles: string[] | undefined;
    token: string | undefined;
    id: number;

    constructor(data?: IUserLoginInfoDto) {
        if (data) {
            for (var property in data) {
                if (data.hasOwnProperty(property)) (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.name = _data['name'];
            this.surname = _data['surname'];
            this.userName = _data['userName'];
            this.emailAddress = _data['emailAddress'];
            this.companyUuid = _data['companyUuid'];
            this.profilePictureId = _data['profilePictureId'];
            this.partnerId = _data['partnerId'];
            this.providerId = _data['providerId'];
            if (Array.isArray(_data['details'])) {
                this.details = [] as any;
                for (let item of _data['details']) this.details.push(RollDetails.fromJS(item));
            }
            this.partnerName = _data['partnerName'];
            this.isImpersonator = _data['isImpersonator'];
            this.userExperience = _data['userExperience'];
            this.isInvestOwner = _data['isInvestOwner'];
            this.uuid = _data['uuid'];
            if (Array.isArray(_data['marketplaceRoles'])) {
                this.marketplaceRoles = [] as any;
                for (let item of _data['marketplaceRoles']) this.marketplaceRoles.push(item);
            }
            this.id = _data['id'];
        }
    }

    static fromJS(data: any): UserLoginInfoDto {
        data = typeof data === 'object' ? data : {};
        let result = new UserLoginInfoDto();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data['name'] = this.name;
        data['surname'] = this.surname;
        data['userName'] = this.userName;
        data['emailAddress'] = this.emailAddress;
        data['companyUuid'] = this.companyUuid;
        data['profilePictureId'] = this.profilePictureId;
        data['partnerId'] = this.partnerId;
        data['providerId'] = this.providerId;
        if (Array.isArray(this.details)) {
            data['details'] = [];
            for (let item of this.details) data['details'].push(item.toJSON());
        }
        data['partnerName'] = this.partnerName;
        data['isImpersonator'] = this.isImpersonator;
        data['userExperience'] = this.userExperience;
        data['isInvestOwner'] = this.isInvestOwner;
        data['uuid'] = this.uuid;
        if (Array.isArray(this.marketplaceRoles)) {
            data['marketplaceRoles'] = [];
            for (let item of this.marketplaceRoles) data['marketplaceRoles'].push(item);
        }
        data['id'] = this.id;
        return data;
    }

    clone(): UserLoginInfoDto {
        const json = this.toJSON();
        let result = new UserLoginInfoDto();
        result.init(json);
        return result;
    }
}

interface IUserLoginInfoDto {
    name: string | undefined;
    surname: string | undefined;
    userName: string | undefined;
    emailAddress: string | undefined;
    companyUuid: string | undefined;
    profilePictureId: string | undefined;
    partnerId: number | undefined;
    providerId: number | undefined;
    details: RollDetails[] | undefined;
    partnerName: string | undefined;
    isImpersonator: boolean;
    userExperience: string | undefined;
    isInvestOwner: boolean;
    uuid: string | undefined;
    marketplaceRoles: string[] | undefined;
    id: number;
    token: string | undefined;
}

@Injectable()
export class BypassedOrderServiceProxy {
    private http: HttpClient;
    private readonly baseUrl: string;
    protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

    constructor(@Inject(HttpClient) http: HttpClient, private readonly injector: Injector) {
        this.http = http;
        const { backendUrlOutsideLoadBalancer} = this.injector.get(ENVIRONMENT);
        this.baseUrl = backendUrlOutsideLoadBalancer;
    }

    /**
     * @param spmId (optional)
     * @param pmId (optional)
     * @return Success
     */
    getPartnerManagementReport(
        spmId: number | undefined,
        pmId: number | undefined,
        token: string
    ): Observable<PartnerManagementReportOutput[]> {
        let url_ = this.baseUrl + '/api/services/app/Order/GetPartnerManagementReport?';
        if (spmId === null) throw new Error("The parameter 'spmId' cannot be null.");
        else if (spmId !== undefined) url_ += 'spmId=' + encodeURIComponent('' + spmId) + '&';
        if (pmId === null) throw new Error("The parameter 'pmId' cannot be null.");
        else if (pmId !== undefined) url_ += 'pmId=' + encodeURIComponent('' + pmId) + '&';
        url_ = url_.replace(/[?&]$/, '');

        let options_: any = {
            observe: 'response',
            responseType: 'blob',
            withCredentials: true,
            headers: new HttpHeaders({
                Accept: 'text/plain',
                'Ad-Authorization': token,
            }),
        };

        return this.http
            .request('get', url_, options_)
            .pipe(
                _observableMergeMap((response_: any) => {
                    return this.processGetPartnerManagementReport(response_);
                })
            )
            .pipe(
                _observableCatch((response_: any) => {
                    if (response_ instanceof HttpResponseBase) {
                        try {
                            return this.processGetPartnerManagementReport(<any>response_);
                        } catch (e) {
                            return <Observable<PartnerManagementReportOutput[]>>(<any>_observableThrow(e));
                        }
                    } else return <Observable<PartnerManagementReportOutput[]>>(<any>_observableThrow(response_));
                })
            );
    }

    protected processGetPartnerManagementReport(response: HttpResponseBase): Observable<PartnerManagementReportOutput[]> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body : (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {};
        if (response.headers) {
            for (let key of response.headers.keys()) {
                _headers[key] = response.headers.get(key);
            }
        }
        if (status === 200) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    let result200: any = null;
                    let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
                    if (Array.isArray(resultData200)) {
                        result200 = [] as any;
                        for (let item of resultData200) result200.push(PartnerManagementReportOutput.fromJS(item));
                    }
                    return _observableOf(result200);
                })
            );
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    return throwException('An unexpected server error occurred.', status, _responseText, _headers);
                })
            );
        }
        return _observableOf<PartnerManagementReportOutput[]>([]);
    }

    /**
     * @param supportId (optional)
     * @return Success
     */
    getActivityPartnerSalesDashboard(supportId: number | undefined, token: string): Observable<ActivityPartnerSalesDashboardOutput[]> {
        let url_ = this.baseUrl + '/api/services/app/Order/GetActivityPartnerSalesDashboard?';
        if (supportId === null) throw new Error("The parameter 'supportId' cannot be null.");
        else if (supportId !== undefined) url_ += 'supportId=' + encodeURIComponent('' + supportId) + '&';
        url_ = url_.replace(/[?&]$/, '');

        let options_: any = {
            observe: 'response',
            responseType: 'blob',
            withCredentials: true,
            headers: new HttpHeaders({
                Accept: 'text/plain',
                'Ad-Authorization': token,
            }),
        };

        return this.http
            .request('get', url_, options_)
            .pipe(
                _observableMergeMap((response_: any) => {
                    return this.processGetActivityPartnerSalesDashboard(response_);
                })
            )
            .pipe(
                _observableCatch((response_: any) => {
                    if (response_ instanceof HttpResponseBase) {
                        try {
                            return this.processGetActivityPartnerSalesDashboard(<any>response_);
                        } catch (e) {
                            return <Observable<ActivityPartnerSalesDashboardOutput[]>>(<any>_observableThrow(e));
                        }
                    } else return <Observable<ActivityPartnerSalesDashboardOutput[]>>(<any>_observableThrow(response_));
                })
            );
    }

    protected processGetActivityPartnerSalesDashboard(response: HttpResponseBase): Observable<ActivityPartnerSalesDashboardOutput[]> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body : (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {};
        if (response.headers) {
            for (let key of response.headers.keys()) {
                _headers[key] = response.headers.get(key);
            }
        }
        if (status === 200) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    let result200: any = null;
                    let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
                    if (Array.isArray(resultData200)) {
                        result200 = [] as any;
                        for (let item of resultData200) result200.push(ActivityPartnerSalesDashboardOutput.fromJS(item));
                    }
                    return _observableOf(result200);
                })
            );
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    return throwException('An unexpected server error occurred.', status, _responseText, _headers);
                })
            );
        }
        return _observableOf<ActivityPartnerSalesDashboardOutput[]>([]);
    }
}

@Injectable()
export class BypassedCommissionChurnReportServiceProxy {
    private http: HttpClient;
    private readonly baseUrl: string;
    protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

    constructor(@Inject(HttpClient) http: HttpClient, private readonly injector: Injector) {
        this.http = http;
        const { backendUrlOutsideLoadBalancer} = this.injector.get(ENVIRONMENT);
        this.baseUrl = backendUrlOutsideLoadBalancer;
    }
    /**
     * @param month (optional)
     * @param year (optional)
     * @param providerId (optional)
     * @param partnerId (optional)
     * @param supportId (optional)
     * @param varianceValue (optional)
     * @param varienceDirection (optional)
     * @param includeCommissionTermOnPayment (optional)
     * @param isMicrocorpRevenue (optional)
     * @param isMcUser (optional)
     * @return Success
     */
    getCommissionChurns(
        month: number | undefined,
        year: number | undefined,
        providerId: number | undefined,
        partnerId: number | undefined,
        supportId: number | undefined,
        varianceValue: number | undefined,
        varienceDirection: number | undefined,
        includeCommissionTermOnPayment: boolean | undefined,
        isMicrocorpRevenue: boolean | undefined,
        isMcUser: boolean | undefined,
        token: string
    ): Observable<CommissionChurnDto[]> {
        let url_ = this.baseUrl + '/api/services/app/CommissionChurnReport/GetCommissionChurns?';
        if (month === null) throw new Error("The parameter 'month' cannot be null.");
        else if (month !== undefined) url_ += 'Month=' + encodeURIComponent('' + month) + '&';
        if (year === null) throw new Error("The parameter 'year' cannot be null.");
        else if (year !== undefined) url_ += 'Year=' + encodeURIComponent('' + year) + '&';
        if (providerId === null) throw new Error("The parameter 'providerId' cannot be null.");
        else if (providerId !== undefined) url_ += 'ProviderId=' + encodeURIComponent('' + providerId) + '&';
        if (partnerId === null) throw new Error("The parameter 'partnerId' cannot be null.");
        else if (partnerId !== undefined) url_ += 'PartnerId=' + encodeURIComponent('' + partnerId) + '&';
        if (supportId === null) throw new Error("The parameter 'supportId' cannot be null.");
        else if (supportId !== undefined) url_ += 'SupportId=' + encodeURIComponent('' + supportId) + '&';
        if (varianceValue === null) throw new Error("The parameter 'varianceValue' cannot be null.");
        else if (varianceValue !== undefined) url_ += 'VarianceValue=' + encodeURIComponent('' + varianceValue) + '&';
        if (varienceDirection === null) throw new Error("The parameter 'varienceDirection' cannot be null.");
        else if (varienceDirection !== undefined) url_ += 'VarienceDirection=' + encodeURIComponent('' + varienceDirection) + '&';
        if (includeCommissionTermOnPayment === null) throw new Error("The parameter 'includeCommissionTermOnPayment' cannot be null.");
        else if (includeCommissionTermOnPayment !== undefined)
            url_ += 'IncludeCommissionTermOnPayment=' + encodeURIComponent('' + includeCommissionTermOnPayment) + '&';
        if (isMicrocorpRevenue === null) throw new Error("The parameter 'isMicrocorpRevenue' cannot be null.");
        else if (isMicrocorpRevenue !== undefined) url_ += 'IsMicrocorpRevenue=' + encodeURIComponent('' + isMicrocorpRevenue) + '&';
        if (isMcUser === null) throw new Error("The parameter 'isMcUser' cannot be null.");
        else if (isMcUser !== undefined) url_ += 'IsMcUser=' + encodeURIComponent('' + isMcUser) + '&';
        url_ = url_.replace(/[?&]$/, '');

        let options_: any = {
            observe: 'response',
            responseType: 'blob',
            withCredentials: true,
            headers: new HttpHeaders({
                Accept: 'text/plain',
                'Ad-Authorization': token,
            }),
        };

        return this.http
            .request('get', url_, options_)
            .pipe(
                _observableMergeMap((response_: any) => {
                    return this.processGetCommissionChurns(response_);
                })
            )
            .pipe(
                _observableCatch((response_: any) => {
                    if (response_ instanceof HttpResponseBase) {
                        try {
                            return this.processGetCommissionChurns(<any>response_);
                        } catch (e) {
                            return <Observable<CommissionChurnDto[]>>(<any>_observableThrow(e));
                        }
                    } else return <Observable<CommissionChurnDto[]>>(<any>_observableThrow(response_));
                })
            );
    }

    protected processGetCommissionChurns(response: HttpResponseBase): Observable<CommissionChurnDto[]> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body : (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {};
        if (response.headers) {
            for (let key of response.headers.keys()) {
                _headers[key] = response.headers.get(key);
            }
        }
        if (status === 200) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    let result200: any = null;
                    let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
                    if (Array.isArray(resultData200)) {
                        result200 = [] as any;
                        for (let item of resultData200) result200.push(CommissionChurnDto.fromJS(item));
                    }
                    return _observableOf(result200);
                })
            );
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    return throwException('An unexpected server error occurred.', status, _responseText, _headers);
                })
            );
        }
        return _observableOf<CommissionChurnDto[]>([]);
    }

    /**
     * @param month (optional)
     * @param year (optional)
     * @param providerId (optional)
     * @param partnerId (optional)
     * @param supportId (optional)
     * @param varianceValue (optional)
     * @param varienceDirection (optional)
     * @param includeCommissionTermOnPayment (optional)
     * @param isMicrocorpRevenue (optional)
     * @param isMcUser (optional)
     * @return Success
     */
    getChurnDashboardStatistics(
        month: number | undefined,
        year: number | undefined,
        providerId: number | undefined,
        partnerId: number | undefined,
        supportId: number | undefined,
        varianceValue: number | undefined,
        varienceDirection: number | undefined,
        includeCommissionTermOnPayment: boolean | undefined,
        isMicrocorpRevenue: boolean | undefined,
        isMcUser: boolean | undefined,
        token: string
    ): Observable<ChurnDashboardStatisticsDto> {
        let url_ = this.baseUrl + '/api/services/app/CommissionChurnReport/GetChurnDashboardStatistics?';
        if (month === null) throw new Error("The parameter 'month' cannot be null.");
        else if (month !== undefined) url_ += 'Month=' + encodeURIComponent('' + month) + '&';
        if (year === null) throw new Error("The parameter 'year' cannot be null.");
        else if (year !== undefined) url_ += 'Year=' + encodeURIComponent('' + year) + '&';
        if (providerId === null) throw new Error("The parameter 'providerId' cannot be null.");
        else if (providerId !== undefined) url_ += 'ProviderId=' + encodeURIComponent('' + providerId) + '&';
        if (partnerId === null) throw new Error("The parameter 'partnerId' cannot be null.");
        else if (partnerId !== undefined) url_ += 'PartnerId=' + encodeURIComponent('' + partnerId) + '&';
        if (supportId === null) throw new Error("The parameter 'supportId' cannot be null.");
        else if (supportId !== undefined) url_ += 'SupportId=' + encodeURIComponent('' + supportId) + '&';
        if (varianceValue === null) throw new Error("The parameter 'varianceValue' cannot be null.");
        else if (varianceValue !== undefined) url_ += 'VarianceValue=' + encodeURIComponent('' + varianceValue) + '&';
        if (varienceDirection === null) throw new Error("The parameter 'varienceDirection' cannot be null.");
        else if (varienceDirection !== undefined) url_ += 'VarienceDirection=' + encodeURIComponent('' + varienceDirection) + '&';
        if (includeCommissionTermOnPayment === null) throw new Error("The parameter 'includeCommissionTermOnPayment' cannot be null.");
        else if (includeCommissionTermOnPayment !== undefined)
            url_ += 'IncludeCommissionTermOnPayment=' + encodeURIComponent('' + includeCommissionTermOnPayment) + '&';
        if (isMicrocorpRevenue === null) throw new Error("The parameter 'isMicrocorpRevenue' cannot be null.");
        else if (isMicrocorpRevenue !== undefined) url_ += 'IsMicrocorpRevenue=' + encodeURIComponent('' + isMicrocorpRevenue) + '&';
        if (isMcUser === null) throw new Error("The parameter 'isMcUser' cannot be null.");
        else if (isMcUser !== undefined) url_ += 'IsMcUser=' + encodeURIComponent('' + isMcUser) + '&';
        url_ = url_.replace(/[?&]$/, '');

        let options_: any = {
            observe: 'response',
            responseType: 'blob',
            withCredentials: true,
            headers: new HttpHeaders({
                Accept: 'text/plain',
                'Ad-Authorization': token,
            }),
        };

        return this.http
            .request('get', url_, options_)
            .pipe(
                _observableMergeMap((response_: any) => {
                    return this.processGetChurnDashboardStatistics(response_);
                })
            )
            .pipe(
                _observableCatch((response_: any) => {
                    if (response_ instanceof HttpResponseBase) {
                        try {
                            return this.processGetChurnDashboardStatistics(<any>response_);
                        } catch (e) {
                            return <Observable<ChurnDashboardStatisticsDto>>(<any>_observableThrow(e));
                        }
                    } else return <Observable<ChurnDashboardStatisticsDto>>(<any>_observableThrow(response_));
                })
            );
    }

    protected processGetChurnDashboardStatistics(response: HttpResponseBase): Observable<ChurnDashboardStatisticsDto> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body : (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {};
        if (response.headers) {
            for (let key of response.headers.keys()) {
                _headers[key] = response.headers.get(key);
            }
        }
        if (status === 200) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    let result200: any = null;
                    let resultData200 = _responseText === '' ? null : JSON.parse(_responseText, this.jsonParseReviver);
                    result200 = ChurnDashboardStatisticsDto.fromJS(resultData200);
                    return _observableOf(result200);
                })
            );
        } else if (status !== 200 && status !== 204) {
            return blobToText(responseBlob).pipe(
                _observableMergeMap((_responseText) => {
                    return throwException('An unexpected server error occurred.', status, _responseText, _headers);
                })
            );
        }
        return _observableOf<ChurnDashboardStatisticsDto>(<any>null);
    }
}
